/*
 * The skin shelf (Skins, mockup 37, ENG-739): every skin this listener owns,
 * opened with "See all" on their own profile card. Backend ENG-740 serves it
 * as `GET /me/skins/shelf?category=all|earned|bought` (schema `SkinShelf`):
 *
 *   { category, counts: { all, earned, bought },   // always over every owned skin
 *     skins: SkinsOwnedSkin[],                     // newest first, filtered
 *     equipped: { [slot]: skinId },
 *     earnable: [{ skin, howToEarn, achievementKey?, progress?: { current, target },
 *                  availableUntil? }],             // [] for "bought"
 *     locked: SkinShopItem[],                      // FX items for sale, not owned
 *     newlyEarned: string[] }                      // ids this read just granted
 *
 *   SkinsOwnedSkin = { skin: SkinsSkin, editionNumber?, source: grant | purchase |
 *                      earned | default, acquiredAt, equipped }
 *
 * "earned" is source `earned` and "bought" is source `purchase`; granted and
 * default skins show under "all" only. Wearing and taking off a skin is
 * `PUT /me/skins/equipped/{type}` with `{ skinId }` and
 * `DELETE /me/skins/equipped/{type}` (ENG-732).
 *
 * Vibe sets and boost animations (ENG-815, ENG-816) are on the shelf too: the
 * owned ones in `skins` (type `vibe_set` / `boost_animation`, with
 * `fxPackageId`; the two free defaults always among them), and the ones for
 * sale that the listener doesn't own in `locked`, read like shop items
 * (skinShop.js). They are worn in their own slots, which are their types.
 */
import config from '../config.js';
import { fetchWrapper } from './fetchWrapper.js';
import { intlFormatter } from './intl.js';
import { toDate } from './formatters.js';
import { normalizeCardSkin } from './listenerCard.js';
import { FX_ITEM_TYPES } from './fxItems.js';
import { normalizeShopItem } from './skinShop.js';

export const SHELF_CATEGORIES = ['all', 'earned', 'bought'];

/** How many skins the shelf shows before "See N more". */
export const SHELF_PAGE_SIZE = 6;

const SOURCES = ['grant', 'purchase', 'earned', 'default'];
const SKIN_TYPES = ['name_paint', 'now_playing_card', ...FX_ITEM_TYPES];

const count = (value) => {
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : 0;
};

/**
 * One `SkinsOwnedSkin`, in the shape the card's preview and tag take
 * (`{ skin, editionNumber, earned, equipped }`) plus its `source`; null when
 * it has no usable skin.
 */
export const normalizeOwnedSkin = (input) => {
  const source = SOURCES.includes(input?.source) ? input.source : 'grant';
  const cardSkin = normalizeCardSkin({ ...input, earned: source === 'earned' });
  if (!cardSkin) return null;
  return { ...cardSkin, source };
};

/** One `SkinEarnable`, checked; null when it has no usable skin. */
export const normalizeEarnable = (input) => {
  const cardSkin = normalizeCardSkin({ skin: input?.skin });
  if (!cardSkin) return null;
  const target = count(input.progress?.target);
  const until = typeof input.availableUntil === 'string' && !Number.isNaN(Date.parse(input.availableUntil))
    ? input.availableUntil
    : null;
  return {
    skin: cardSkin.skin,
    howToEarn: typeof input.howToEarn === 'string' ? input.howToEarn : '',
    progress: target > 0 ? { current: Math.min(count(input.progress.current), target), target } : null,
    availableUntil: until,
  };
};

/** The `SkinShelf` payload, checked and tidied; null when it is not one. */
export const normalizeSkinShelf = (input) => {
  if (!input || typeof input !== 'object' || !Array.isArray(input.skins)) return null;
  const equipped = {};
  if (input.equipped && typeof input.equipped === 'object') {
    Object.entries(input.equipped).forEach(([slot, id]) => {
      if (typeof id === 'string' && id) equipped[slot] = id;
    });
  }
  const skins = input.skins.map(normalizeOwnedSkin).filter(Boolean)
    // `equipped` is the slot map's word; a skin's own flag can lag it.
    .map((owned) => ({ ...owned, equipped: equipped[owned.skin.type] === owned.skin.id }));
  const counts = input.counts && typeof input.counts === 'object' ? input.counts : {};
  return {
    category: SHELF_CATEGORIES.includes(input.category) ? input.category : 'all',
    counts: { all: count(counts.all), earned: count(counts.earned), bought: count(counts.bought) },
    skins,
    equipped,
    earnable: Array.isArray(input.earnable) ? input.earnable.map(normalizeEarnable).filter(Boolean) : [],
    // Only FX items are sold from the shelf; anything else in the list is skipped.
    locked: Array.isArray(input.locked)
      ? input.locked.map(normalizeShopItem).filter((item) => item && FX_ITEM_TYPES.includes(item.itemType))
      : [],
    newlyEarned: Array.isArray(input.newlyEarned) ? input.newlyEarned.filter((id) => typeof id === 'string' && id) : [],
  };
};

/**
 * Fetches the caller's shelf for `category` ('all', 'earned' or 'bought').
 * Rejects with the fetchWrapper error: 400 `invalid_request` for an unknown
 * category, 401 without a session, 503 when the service isn't configured.
 */
export const fetchSkinShelf = async (category = 'all') => {
  if (!SHELF_CATEGORIES.includes(category)) {
    return Promise.reject({ status: 400, code: 'invalid_request', message: 'unknown shelf category' });
  }
  const data = await fetchWrapper.get(`${config.apiBaseUrl}/me/skins/shelf?category=${category}`);
  const shelf = normalizeSkinShelf(data);
  if (!shelf) return Promise.reject({ status: 500, code: null, message: 'malformed skin shelf' });
  return shelf;
};

const checkSlot = (type) => (SKIN_TYPES.includes(type)
  ? null
  : { status: 400, code: 'unknown_skin_type', message: 'unknown skin type' });

/** Wears an owned skin in its slot (its type), replacing the one there. */
export const equipSkin = (skin) => {
  const bad = checkSlot(skin?.type);
  if (bad) return Promise.reject(bad);
  return fetchWrapper.put(`${config.apiBaseUrl}/me/skins/equipped/${skin.type}`, { skinId: skin.id });
};

/** Empties the slot `skin` is worn in. */
export const unequipSkin = (skin) => {
  const bad = checkSlot(skin?.type);
  if (bad) return Promise.reject(bad);
  return fetchWrapper.delete(`${config.apiBaseUrl}/me/skins/equipped/${skin.type}`);
};

/** The names of the skins in `ids` (newlyEarned), from the shelf's owned skins. */
export const newlyEarnedNames = (shelf, ids) => {
  if (!shelf || !ids?.length) return [];
  return ids
    .map((id) => shelf.skins.find((owned) => owned.skin.id === id)?.skin.name)
    .filter(Boolean);
};

/*
 * An earn rule's deadline in the viewer's time zone (ENG-876). The backend
 * writes the Founding Listener cutoff into `howToEarn` itself, in Pacific
 * time ("Subscribe to Listener Premium or Performer Premium before April 1,
 * 2027."; skins.howToEarn in the backend), and sends the instant as
 * `availableUntil`. The shelf shows that date in the viewer's own zone,
 * formatted from `availableUntil`, in place of the server's text.
 */

/** The zone the backend writes a deadline in (skins.cutoffZone). */
export const SERVER_DEADLINE_ZONE = 'America/Los_Angeles';

const partsOf = (date, options) => Object.fromEntries(
  intlFormatter(Intl.DateTimeFormat, 'en-US', options).formatToParts(date).map(({ type, value }) => [type, value]),
);

/** Whether `date` is midnight on the clock in `timeZone` (undefined: the viewer's). */
const isMidnightIn = (date, timeZone) => {
  if (date.getUTCMilliseconds() !== 0) return false;
  const parts = partsOf(date, { timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return Number(parts.hour) % 24 === 0 && Number(parts.minute) === 0 && Number(parts.second) === 0;
};

/**
 * The deadline `until` as the backend writes it into `howToEarn` (Go's
 * "January 2, 2006", or "January 2, 2006 at 3:04 PM MST" when it is not
 * midnight Pacific): "April 1, 2027", "April 1, 2027 at 3:30 PM PDT". ''
 * when `until` doesn't parse.
 */
export const serverDeadlineText = (until) => {
  const date = toDate(until);
  if (!date) return '';
  const zone = SERVER_DEADLINE_ZONE;
  const d = partsOf(date, { timeZone: zone, year: 'numeric', month: 'long', day: 'numeric' });
  const day = `${d.month} ${d.day}, ${d.year}`;
  if (isMidnightIn(date, zone)) return day;
  const tm = partsOf(date, { timeZone: zone, hour: 'numeric', minute: '2-digit', timeZoneName: 'short', hour12: true });
  return `${day} at ${tm.hour}:${tm.minute} ${tm.dayPeriod} ${tm.timeZoneName}`;
};

/**
 * The deadline `until` in the viewer's time zone (`format`'s): its date,
 * "April 1, 2027", when it falls at midnight there; otherwise with its time
 * and zone, "March 31, 2027 at 9:00 PM HST", so a deadline that is late on
 * the evening before never reads as a day later than it is. '' when `until`
 * doesn't parse.
 */
export const localDeadlineText = (t, format, until) => {
  const date = toDate(until);
  if (!date) return '';
  const day = format.date(date, { year: 'numeric', month: 'long', day: 'numeric' });
  if (isMidnightIn(date, format.timeZone)) return day;
  return t('stream.shelf.deadlineAt', { date: day, time: format.time(date, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) });
};

/**
 * An earnable skin's `howToEarn` with the server's Pacific-time deadline
 * (serverDeadlineText) replaced by the viewer's (localDeadlineText). Text
 * without a deadline, or whose deadline isn't written the server's way, is
 * returned as it is.
 */
export const localizeHowToEarn = (t, format, howToEarn, until) => {
  if (!howToEarn || !until) return howToEarn ?? '';
  const server = serverDeadlineText(until);
  if (!server || !howToEarn.includes(server)) return howToEarn;
  return howToEarn.split(server).join(localDeadlineText(t, format, until));
};
